import _ from 'lodash';

import { Subject, SUBJECTS } from '@repo/validation';

/**
 * Joins an array of names into a single string.
 * @param names An array of names to join
 * @returns A string containing all the names joined by a space
 */
export const joinNames = (...names: (string | number | undefined)[]) =>
  names.filter(x => x !== undefined && x !== '').join(' ');

export interface NamedStaff {
  firstName?: string | undefined;
  lastName?: string | undefined;
  orgStaffId?: string | undefined;
}

/**
 * Joins a staff member's name with their org ID.
 * @param staff The staff member to name
 * @returns The name to show wherever a staff member is listed
 */
export const formatStaffName = (staff: NamedStaff) =>
  joinNames(staff.firstName, staff.lastName, staff.orgStaffId && `(${staff.orgStaffId})`);

/**
 * Returns the label for the specified subject key
 * @param subject Key for the subject
 * @returns Label for the subject
 */
export const getSubjectLabel = (subject: Subject) => SUBJECTS.find(x => x.key === subject)?.label;

/**
 * Converts a machine-readable value to sentence case for display.
 * Splits on the usual word boundaries, so `UPPER_SNAKE`, `camelCase` and
 * `kebab-case` all read as prose.
 * @param value Value to convert
 * @returns The value in sentence case
 */
export const toSentenceCase = (value: string) => _.upperFirst(_.lowerCase(value));

/**
 * English has two plural categories, `one` and `other`, so every count but one
 * takes the plural form — zero included.
 */
const PLURAL_RULES = new Intl.PluralRules('en-AU');

/**
 * Returns the form of a noun that agrees with a count.
 * @param count Quantity the noun describes
 * @param singular Singular form of the noun
 * @param plural Plural form, when appending an `s` would be wrong
 * @returns The noun in the form that agrees with the count
 */
export const pluralise = (count: number, singular: string, plural = `${singular}s`) =>
  PLURAL_RULES.select(count) === 'one' ? singular : plural;

/**
 * Renders a count alongside the form of the noun that agrees with it.
 * @param count Quantity the noun describes
 * @param singular Singular form of the noun
 * @param plural Plural form, when appending an `s` would be wrong
 * @returns The count and noun, separated by a space
 */
export const formatCount = (count: number, singular: string, plural?: string) =>
  `${count} ${pluralise(count, singular, plural)}`;

/**
 * Names pages the way a sentence does: `page 3`, `pages 3 and 4`, `pages 3, 4
 * and 7`, in the order given, or `no pages`.
 */
export const formatPageList = (pages: readonly number[]) => {
  if (pages.length === 0) {
    return 'no pages';
  }
  const leading = pages.slice(0, -1).join(', ');
  const last = pages.slice(-1).join('');
  return `${pluralise(pages.length, 'page')} ${leading ? `${leading} and ${last}` : last}`;
};
